import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, Check, CheckCheck, Trash2, X,
  AlertTriangle, CheckCircle2, Info, AlertCircle
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const SEVERITY_CONFIG = {
  success: { icon: CheckCircle2, color: 'text-green-500', bg: 'bg-green-100 dark:bg-green-900/40' },
  error: { icon: AlertCircle, color: 'text-red-500', bg: 'bg-red-100 dark:bg-red-900/40' },
  warning: { icon: AlertTriangle, color: 'text-amber-500', bg: 'bg-amber-100 dark:bg-amber-900/40' },
  info: { icon: Info, color: 'text-blue-500', bg: 'bg-blue-100 dark:bg-blue-900/40' },
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('einvoice_token');

  const headers = { Authorization: `Bearer ${token}` };

  const fetchCount = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/einvoice/notifications/count`, { headers });
      setUnreadCount(data.unread_count);
    } catch { /* ignore */ }
  }, [token]);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/einvoice/notifications/list?limit=20`, { headers });
      setNotifications(data.notifications);
      setUnreadCount(data.unread_count);
    } catch { /* ignore */ }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  const markRead = async (id) => {
    try {
      await axios.put(`${API_URL}/api/einvoice/notifications/${id}/read`, {}, { headers });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { /* ignore */ }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API_URL}/api/einvoice/notifications/read-all`, {}, { headers });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch { /* ignore */ }
  };

  const deleteNotif = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/einvoice/notifications/${id}`, { headers });
      setNotifications(prev => prev.filter(n => n.id !== id));
      fetchCount();
    } catch { /* ignore */ }
  };

  const handleClick = (notif) => {
    if (!notif.read) markRead(notif.id);
    if (notif.link) {
      navigate(notif.link);
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
        data-testid="notifications-btn"
      >
        <Bell className="w-[18px] h-[18px]" />
        {unreadCount > 0 && (
          <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-1" data-testid="notif-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute top-full right-0 mt-2 w-[360px] bg-white dark:bg-[#1f2937] border border-slate-200 dark:border-white/10 rounded-xl shadow-2xl z-20 overflow-hidden" data-testid="notif-panel">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-white/5">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Notifications</h3>
              <div className="flex items-center gap-1">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllRead}
                    className="flex items-center gap-1 text-xs text-[#4169E1] hover:text-blue-700 dark:hover:text-blue-300 px-2 py-1 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                    data-testid="mark-all-read-btn"
                  >
                    <CheckCheck className="w-3.5 h-3.5" /> Mark all read
                  </button>
                )}
                <button onClick={() => setOpen(false)} className="p-1 text-slate-400 dark:text-gray-500 hover:text-slate-600 dark:hover:text-gray-300 rounded-md">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Notification List */}
            <div className="max-h-[400px] overflow-y-auto" data-testid="notif-list">
              {loading && notifications.length === 0 ? (
                <div className="py-8 text-center text-slate-400 dark:text-gray-500 text-sm">Loading...</div>
              ) : notifications.length === 0 ? (
                <div className="py-8 text-center">
                  <Bell className="w-8 h-8 mx-auto mb-2 text-slate-300 dark:text-gray-600" />
                  <p className="text-sm text-slate-400 dark:text-gray-500">No notifications yet</p>
                </div>
              ) : (
                notifications.map(notif => {
                  const config = SEVERITY_CONFIG[notif.severity] || SEVERITY_CONFIG.info;
                  const Icon = config.icon;
                  return (
                    <div
                      key={notif.id}
                      className={`flex gap-3 px-4 py-3 border-b border-slate-100 dark:border-white/5 last:border-0 transition-colors cursor-pointer group ${
                        notif.read
                          ? 'bg-transparent hover:bg-slate-50 dark:hover:bg-white/5'
                          : 'bg-blue-50/50 dark:bg-blue-900/10 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                      }`}
                      onClick={() => handleClick(notif)}
                      data-testid={`notif-item-${notif.id}`}
                    >
                      <div className={`w-8 h-8 rounded-lg ${config.bg} flex items-center justify-center shrink-0 mt-0.5`}>
                        <Icon className={`w-4 h-4 ${config.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm font-medium leading-tight ${notif.read ? 'text-slate-600 dark:text-gray-400' : 'text-slate-900 dark:text-white'}`}>
                            {notif.title}
                          </p>
                          {!notif.read && <div className="w-2 h-2 bg-[#4169E1] rounded-full shrink-0 mt-1" />}
                        </div>
                        <p className="text-xs text-slate-500 dark:text-gray-500 mt-0.5 line-clamp-2">{notif.message}</p>
                        <p className="text-[10px] text-slate-400 dark:text-gray-600 mt-1">{timeAgo(notif.created_at)}</p>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteNotif(notif.id); }}
                        className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 dark:text-gray-600 hover:text-red-500 dark:hover:text-red-400 rounded transition-all shrink-0"
                        data-testid={`notif-delete-${notif.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
